import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { INFORMATION_CONTENT } from 'constants/questionTypes';
import {
  getSection,
  getQuestionIndex,
  getQuestionCount,
  getCourseQuestionCount,
  sectionExists
} from 'store/sections/selectors';

import {
  getBackground,
  getLogo,
  isMainLogoShown,
  isAllQuestionsOnOnePage,
  isShowLearningHintsEnabled,
  isQuestionPoolEnabled,
  getQuestionPoolSize
} from 'store/settings/selectors';
import withFallbackToNotFound from 'components/hocs/withFallbackToNotFound';
import withNavigation from 'components/hocs/withNavigation';
import Question from 'components/questions/Question';
import * as questionActions from 'store/questions/actions';
import {
  getQuestion,
  isQuestionAnswered,
  isQuestionAnsweredCorrectly,
  questionExists,
  getQuestionResponse
} from 'store/questions/selectors';
import Feedback from 'components/question/Feedback';
import { getCourseTitle, isAttemptSubmitted, getCourseQuestionIndex } from 'store/course/selectors';
import { RootAppState } from 'store/types';
import { hasGuidedReattempt } from 'store/common/selectors';
import { QuestionPage, QuestionPageProps } from './QuestionPage';
import ActionsBlockSubmitOnce from './actionsBlock/ActionsBlockSubmitOnce';

interface QuestionPagePropsSubmitOnce extends QuestionPageProps {
  courseQuestionIndex: number;
  overallNumberOfQuestions: number;
  isCourseSubmitted?: boolean;
  navigateToUrl(url: string): void;
  hasGuidedReattempt: boolean;
}

export class QuestionPageSubmitOnce extends QuestionPage<QuestionPagePropsSubmitOnce> {
  constructor(props: QuestionPagePropsSubmitOnce) {
    super(props);
    this.state = {
      showTooltip: false,
      feedbackContentList: []
    };
  }

  render() {
    const {
      question,
      questionIndex,
      courseQuestionIndex,
      questionCountInSection,
      overallNumberOfQuestions,
      bodyBackground,
      isCourseSubmitted,
      navigateToUrl,
      isCourseOnOnePage,
      isLastBlock,
      hasGuidedReattempt
    } = this.props;

    return (
      <Fragment>
        {this.renderQuestionHeader()}
        <Question
          id={question.id}
          type={question.type}
          allowSubmit={true}
          isAnswered={
            (isCourseSubmitted && question.isAnswered) ||
            (hasGuidedReattempt && !isCourseSubmitted && question.isPreviousAnswerCorrect)
          }
          bodyBackground={bodyBackground}
        />
        {this.renderInformationContentOrHints()}
        {this.renderFeedback()}
        <ActionsBlockSubmitOnce
          navigateToUrl={navigateToUrl}
          question={question}
          goToNextPage={this.navigateToNextPage}
          isNextPageResults={isCourseOnOnePage ? isLastBlock : this.props.isNextPageResults()}
          questionCountInSection={questionCountInSection}
          overallNumberOfQuestions={overallNumberOfQuestions}
          questionIndex={questionIndex}
          courseQuestionIndex={courseQuestionIndex}
          isCourseOnOnePage={isCourseOnOnePage}
        />

        {this.renderVoiceOver()}
      </Fragment>
    );
  }

  canGoToNextQuestion() {
    const { question, isCourseSubmitted } = this.props;
    if (question.type === INFORMATION_CONTENT) {
      return true;
    } else if (isCourseSubmitted) {
      return question.isAnswered;
    } else {
      return false;
    }
  }

  renderFeedback() {
    const { question, isCourseSubmitted, hasGuidedReattempt } = this.props;
    const isVisible = () => {
      if (!isCourseSubmitted) {
        return hasGuidedReattempt && question.isPreviousAnswerCorrect;
      }
      return true;
    };
    if (question.type === INFORMATION_CONTENT || !isVisible()) {
      return <></>;
    }

    return <Feedback question={question} animate={true} delay={200} />;
  }
}

function mapStateToProps(state: RootAppState, ownProps: any) {
  const isOnePage = isAllQuestionsOnOnePage(state);
  const { sectionId, questionId } = (() => {
    if (isOnePage) {
      return ownProps;
    }
    return ownProps.match.params;
  })();
  return {
    logoUrl: getLogo(state),
    logoShown: isMainLogoShown(state),
    section: getSection(state, sectionId),
    courseQuestionIndex: getCourseQuestionIndex(state, questionId),
    questionIndex: getQuestionIndex(state, sectionId, questionId),
    questionCountInSection: getQuestionCount(state, sectionId),
    overallNumberOfQuestions: getCourseQuestionCount(state),
    question: getQuestion(state, questionId),
    isAnswered: isQuestionAnswered(state, questionId),
    isAnsweredCorrectly: isQuestionAnsweredCorrectly(state, questionId),
    bodyBackground: getBackground(state).body,
    title: getCourseTitle(state),
    sectionExists: sectionExists(state, sectionId),
    questionExists: questionExists(state, questionId),
    isCourseSubmitted: isAttemptSubmitted(state),
    questionResponse: getQuestionResponse(state, questionId),
    isCourseOnOnePage: isAllQuestionsOnOnePage(state),
    showLearningHints: isShowLearningHintsEnabled(state),
    isQuestionPool: isQuestionPoolEnabled(state),
    questionPoolSize: getQuestionPoolSize(state),
    hasGuidedReattempt: hasGuidedReattempt(state)
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return { actions: bindActionCreators(questionActions, dispatch) };
}

function predicateForRedirect(props: any) {
  return !props.sectionExists || !props.questionExists;
}

export default withNavigation(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withFallbackToNotFound(QuestionPageSubmitOnce, predicateForRedirect))
);
